import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { User, Loader2, Check, Rows3 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { AppShell } from '../components/AppShell'
import { useUnsavedChanges } from '../hooks/useUnsavedChanges'
import { UnsavedChangesDialog } from '../components/UnsavedChangesDialog'
import { useCurrentUser } from '../contexts/UserContext'
import { UserApi } from '@/services/users/userApi'
import { apiErrorDescription } from '@/services/core/apiError'
import { Card, CardHeader, CardIcon, CardTitle, CardContent } from '@/components/ui/card'
import { FieldLabel } from '../components/FieldLabel'
import { SwitchField } from '../components/SwitchField'
import { useCompactTables } from '../hooks/useCompactTables'
import { useShowHeaderIds } from '../hooks/useShowHeaderIds'

const LOCALE_OPTIONS = [
  { label: 'English (UK)', value: 'en_GB' },
  { label: 'English (US)', value: 'en_US' },
  { label: 'Norwegian', value: 'nb_NO' },
  { label: 'Swedish', value: 'sv_SE' },
  { label: 'Danish', value: 'da_DK' },
  { label: 'German', value: 'de_DE' },
  { label: 'French', value: 'fr_FR' },
  { label: 'Spanish', value: 'es_ES' },
]

const LANGUAGE_OPTIONS = [
  { label: 'English', value: 'en' },
  { label: 'Norsk', value: 'no' },
]

const DEFAULTS = {
  name: '',
  displayName: '',
  locale: 'en_GB',
  compactTables: false,
  showHeaderIds: false,
}

export function UserPreferencesPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const currentUser = useCurrentUser()
  const { compact, setCompact } = useCompactTables()
  const { showIds, setShowIds } = useShowHeaderIds()
  const [defaults, setDefaults] = useState(DEFAULTS)

  const form = useForm({
    defaultValues: defaults,
    onSubmit: ({ value }) => {
      mutation.mutate(value)
    },
  })
  const { isDirty, markClean, blocker } = useUnsavedChanges(form)

  useEffect(() => {
    if (currentUser) {
      const vals = {
        name: currentUser.name,
        displayName: currentUser.displayName,
        locale: currentUser.locale || 'en_GB',
        compactTables: compact,
        showHeaderIds: showIds,
      }
      setDefaults(vals)
      markClean(vals)
      form.reset(vals)
    }
  }, [currentUser, compact, showIds])

  const mutation = useMutation({
    mutationFn: async (input: typeof DEFAULTS) => {
      if (!currentUser?.id) throw new Error('No user')
      await UserApi.updateUser(currentUser.id, {
        name: input.name,
        displayName: input.displayName,
        locale: input.locale,
      })
      // Kept in the user profile rather than on the user itself.
      if (input.compactTables !== compact) await setCompact(input.compactTables)
      if (input.showHeaderIds !== showIds) await setShowIds(input.showHeaderIds)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
      toast.success(t('preferences.savedTitle'), {
        description: t('preferences.savedDetail'),
        duration: 3000,
      })
    },
    onError: (error) => {
      toast.error(t('preferences.saveFailedTitle'), {
        description: apiErrorDescription(error),
        duration: 5000,
      })
    },
  })

  const crumbs = [{ label: t('preferences.title') }]

  return (
    <AppShell crumbs={crumbs}>
      <form
        className="ad-page"
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
      >
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{t('preferences.title')}</h1>
            <p>{t('preferences.subtitle')}</p>
          </div>
        </div>

        <div className="flex gap-[14px] items-start">
          {/* Profile card */}
          <Card>
            <CardHeader>
              <CardTitle>
                <CardIcon icon={User} />
                {t('preferences.profile')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="field-grid">
                <div className="p-field">
                  <label>{t('preferences.nameLabel')}</label>
                  <form.Field name="name">
                    {(field) => (
                      <Input
                        
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        placeholder={t('preferences.namePlaceholder')}
                        style={{ width: '100%' }}
                      />
                    )}
                  </form.Field>
                </div>
                <div className="p-field">
                  <label>{t('preferences.displayName')}</label>
                  <form.Field name="displayName">
                    {(field) => (
                      <Input
                        
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        placeholder={t('preferences.displayNamePlaceholder')}
                        style={{ width: '100%' }}
                      />
                    )}
                  </form.Field>
                </div>
                <div className="p-field">
                  <label>{t('preferences.locale')}</label>
                  <form.Field name="locale">
                    {(field) => (
                      <Select label={t('preferences.locale')} value={field.state.value} onValueChange={(v) => field.handleChange(v ?? '')}>
                        <SelectTrigger>
                          <SelectValue placeholder={t('preferences.localePlaceholder')}>{LOCALE_OPTIONS.find((o) => o.value === field.state.value)?.label ?? null}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {LOCALE_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </form.Field>
                </div>
                <div className="p-field">
                  <label>{t('preferences.language')}</label>
                  <Select label={t('preferences.language')} value={i18n.language || undefined} onValueChange={(v) => { if (v) { i18n.changeLanguage(v); localStorage.setItem('i18nLanguage', v) } }}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('preferences.languagePlaceholder')}>{LANGUAGE_OPTIONS.find((o) => o.value === i18n.language)?.label ?? null}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {LANGUAGE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="p-field">
                  <FieldLabel tooltip={t('preferences.usernameHint')}>
                    {t('preferences.username')}
                  </FieldLabel>
                  <Input
                    
                    value={currentUser?.username ?? ''}
                    disabled
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Display card */}
          <Card>
            <CardHeader>
              <CardTitle>
                <CardIcon icon={Rows3} />
                {t('preferences.display')}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <form.Field name="compactTables">
                {(field) => (
                  <SwitchField
                    label={t('preferences.compactTables')}
                    description={t('preferences.compactTablesHelp')}
                    checked={field.state.value}
                    onCheckedChange={field.handleChange}
                  />
                )}
              </form.Field>
              <form.Field name="showHeaderIds">
                {(field) => (
                  <SwitchField
                    label={t('preferences.showHeaderIds')}
                    description={t('preferences.showHeaderIdsHelp')}
                    checked={field.state.value}
                    onCheckedChange={field.handleChange}
                  />
                )}
              </form.Field>
            </CardContent>
          </Card>
        </div>

        {/* Save bar */}
        <div className={`detail-savebar${isDirty || mutation.isPending ? ' is-visible' : ''}`}>
          <div className="detail-savebar__left">
            {mutation.isPending && (
              <>
                <Loader2 className="animate-spin size-4" />
                {t('common.saving')}
              </>
            )}
          </div>
          <div className="detail-savebar__actions">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              disabled={mutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              loading={mutation.isPending}
            >
              <Check className="size-4" /> {t('common.save')}
            </Button>
          </div>
        </div>
      </form>
      <UnsavedChangesDialog blocker={blocker} />
    </AppShell>
  )
}
